/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/return-await */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import useGetFines from 'hooks/fines/useGetFines';
import { Fine } from 'utils/types';
import useGetOfficerPendingCases from 'hooks/fines/useGetOfficerPendingCases';
import useGetCaseCheckerPendingCases from 'hooks/fines/useGetCaseCheckerPendingCases';
import { useAuth } from './AuthContext';
import keycloak from 'keyClock';

type CaseCheckerFinesView = 'pending' | 'all';

interface FinesContextType {
  currentPage: number;
  pageSize: number;
  fines: Fine[];
  totalPages: number;
  isLoading: boolean;
  goToPage: (pageNumber: number) => void;
  handleFilter: (columnKey: string, value: string) => void;
  handlePageSizeChange: (newSize: number) => void; // 👈 new
  fetchNextFine: (currentFineId: string) => Promise<Fine | null>;
  fetchPreviousFine: (currentFineId: string) => Promise<Fine | null>;
  totalEntries: number;
  isCaseChecker: boolean;
  caseCheckerFinesView: CaseCheckerFinesView;
  setCaseCheckerFinesView: (view: CaseCheckerFinesView) => void;
}

const FinesContext = createContext<FinesContextType | undefined>(undefined);

const FILTER_KEYS = [
  'smsStatus',
  'violationId',
  'vehicleRegistrationNumber',
  'location',
  'id',
  'dateCaptured',
  'dateCreated',
  'ticketNumber',
  'enforcementStatus',
  'status',
] as const;

type FilterKey = (typeof FILTER_KEYS)[number];
type Filters = Record<FilterKey, string>;

const buildInitialFilters = (searchParams: URLSearchParams): Filters => {
  return Object.fromEntries(
    FILTER_KEYS.map((key) => [
      key,
      key === 'vehicleRegistrationNumber'
        ? (searchParams.get(key) || '').toUpperCase()
        : searchParams.get(key) || '',
    ]),
  ) as Filters;
};

const DEFAULT_PAGE_SIZE = 20;
const CASE_CHECKER_REFETCH_INTERVAL = 60000;

export const FinesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page: pageParam } = useParams<{ page?: string }>();

  const pageFromPath = parseInt(pageParam || '1', 10);
  const [currentPage, setCurrentPage] = useState(pageFromPath);

  // 👇 pageSize is now dynamic, seeded from URL param if present
  const [pageSize, setPageSize] = useState<number>(() => {
    const fromUrl = parseInt(searchParams.get('pageSize') || '', 10);
    return fromUrl && [10, 20, 50, 100].includes(fromUrl)
      ? fromUrl
      : DEFAULT_PAGE_SIZE;
  });

  const stablePageRef = useRef(pageFromPath);

  useEffect(() => {
    if (pageParam !== undefined) {
      const page = parseInt(pageParam, 10);
      setCurrentPage(page);
      stablePageRef.current = page;
    }
  }, [pageParam]);

  const [filters, setFilters] = useState<Filters>(() =>
    buildInitialFilters(searchParams),
  );
  const [caseCheckerFinesView, setCaseCheckerFinesViewState] =
    useState<CaseCheckerFinesView>(() =>
      searchParams.get('caseCheckerView') === 'all' ? 'all' : 'pending',
    );

  const { auth } = useAuth();

  const cleanFilters = (filtersParams: Record<string, string>) => {
    return Object.fromEntries(
      Object.entries(filtersParams).filter(([_, value]) => value !== ''),
    );
  };

  const client =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'api-service-client';
  const isOfficer =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('officer');
  const isCaseChecker =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes(
      'case_checker',
    );
  const isCaseCheckerPendingView =
    Boolean(isCaseChecker) && caseCheckerFinesView === 'pending';
  const isCaseCheckerAllFinesView =
    Boolean(isCaseChecker) && caseCheckerFinesView === 'all';
  const effectivePageSize =
    isCaseCheckerPendingView && !searchParams.has('pageSize') ? 20 : pageSize;

  const { data: caseCheckerData, isLoading: caseCheckerLoading } =
    useGetCaseCheckerPendingCases({
      options: {
        page: (currentPage - 1).toString(),
        pageSize: effectivePageSize.toString(),
        filters: cleanFilters(filters),
        enabled: isCaseCheckerPendingView,
        refetchInterval: CASE_CHECKER_REFETCH_INTERVAL,
      },
    });

  const { data: officerData, isLoading: officerLoading } =
    useGetOfficerPendingCases({
      options: {
        page: (currentPage - 1).toString(),
        pageSize: effectivePageSize.toString(),
        filters: cleanFilters(filters),
        enabled: isOfficer && !isCaseChecker,
      },
    });

  const { data: finesData, isLoading: finesLoading } = useGetFines({
    options: {
      pageNumber: (currentPage - 1).toString(),
      pageSize: effectivePageSize.toString(),
      filters: cleanFilters(filters),
      enabled: (!isOfficer && !isCaseChecker) || isCaseCheckerAllFinesView,
      refetchInterval: isCaseCheckerAllFinesView
        ? CASE_CHECKER_REFETCH_INTERVAL
        : undefined,
    },
  });

  const fines = isCaseCheckerPendingView
    ? {
        finesList: caseCheckerData?.finesList?.content ?? [],
        totalPages: caseCheckerData?.finesList?.page?.totalPages ?? 0,
        totalRecords: caseCheckerData?.finesList?.page?.totalElements ?? 0,
      }
    : isOfficer
      ? {
          finesList: officerData?.finesList?.content ?? [],
          totalPages: officerData?.finesList?.totalPages ?? 0,
          totalRecords: officerData?.finesList?.totalElements ?? 0,
        }
      : {
          finesList: finesData?.finesList ?? [],
          totalPages: finesData?.totalPages ?? 0,
          totalRecords: finesData?.totalRecords ?? 0,
        };

  const isLoading = isCaseCheckerPendingView
    ? caseCheckerLoading
    : isOfficer
      ? officerLoading
      : finesLoading;

  const goToPage = (pageNumber: number) => {
    if (fines && pageNumber > 0 && pageNumber <= fines.totalPages) {
      stablePageRef.current = pageNumber;
      setCurrentPage(pageNumber);
    }
  };

  // 👇 Reset to page 1 and update pageSize in state + URL
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    stablePageRef.current = 1;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('pageSize', String(newSize));
      next.set('pageNumber', '1');
      return next;
    });
  };

  const setCaseCheckerFinesView = (view: CaseCheckerFinesView) => {
    setCaseCheckerFinesViewState(view);
    setCurrentPage(1);
    stablePageRef.current = 1;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('caseCheckerView', view);
      next.set('pageNumber', '1');
      return next;
    });
  };

  const handleFilter = (columnKey: string, value: string) => {
    const normalizedValue =
      columnKey === 'vehicleRegistrationNumber' ? value.toUpperCase() : value;
    setFilters((prev) => ({ ...prev, [columnKey]: normalizedValue }));
    setSearchParams((prevParams) => {
      const newSearchParams = new URLSearchParams(prevParams);
      if (normalizedValue) {
        newSearchParams.set(columnKey, normalizedValue);
      } else {
        newSearchParams.delete(columnKey);
      }
      return newSearchParams;
    });
  };

  const isLoadingRef = useRef(isLoading);
  const finesRef = useRef(fines);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    finesRef.current = fines;
  }, [fines]);

  const loadPageAndSelect = (
    targetPage: number,
    selector: (list: Fine[]) => Fine | null,
  ): Promise<Fine | null> => {
    return new Promise((resolve) => {
      stablePageRef.current = targetPage;
      setCurrentPage(targetPage);
      let attempts = 0;
      const maxAttempts = 50;

      const check = () => {
        attempts += 1;
        if (!isLoadingRef.current && attempts > 1) {
          resolve(selector(finesRef.current.finesList));
          return;
        }
        if (attempts >= maxAttempts) {
          resolve(null);
          return;
        }
        setTimeout(check, 100);
      };
      setTimeout(check, 100);
    });
  };

  const matchesFineIdentifier = (fine: Fine, identifier: string) => {
    return (
      fine.id.toString() === identifier ||
      fine.ticketNumber.toString() === identifier
    );
  };

  const fetchNextFine = async (currentFineId: string): Promise<Fine | null> => {
    const list = finesRef.current.finesList;
    const currentIndex = list.findIndex((fine) =>
      matchesFineIdentifier(fine, currentFineId),
    );

    if (currentIndex !== -1 && currentIndex < list.length - 1) {
      return list[currentIndex + 1];
    }

    const nextPage = stablePageRef.current + 1;
    if (nextPage <= finesRef.current.totalPages) {
      return loadPageAndSelect(nextPage, (newList) => newList[0] ?? null);
    }

    return null;
  };

  const fetchPreviousFine = async (
    currentFineId: string,
  ): Promise<Fine | null> => {
    const list = finesRef.current.finesList;
    const currentIndex = list.findIndex((fine) =>
      matchesFineIdentifier(fine, currentFineId),
    );

    if (currentIndex > 0) {
      return list[currentIndex - 1];
    }

    const prevPage = stablePageRef.current - 1;
    if (prevPage >= 1) {
      return loadPageAndSelect(
        prevPage,
        (newList) => newList[newList.length - 1] ?? null,
      );
    }

    return null;
  };

  return (
    <FinesContext.Provider
      value={{
        currentPage,
        pageSize: effectivePageSize,
        fines: fines?.finesList || [],
        totalPages: fines?.totalPages || 0,
        totalEntries: fines?.totalRecords || 0,
        isLoading,
        goToPage,
        handleFilter,
        handlePageSizeChange, // 👈 new
        fetchNextFine,
        fetchPreviousFine,
        isCaseChecker: Boolean(isCaseChecker),
        caseCheckerFinesView,
        setCaseCheckerFinesView,
      }}
    >
      {children}
    </FinesContext.Provider>
  );
};

export const useFines = () => {
  const context = useContext(FinesContext);
  if (context === undefined) {
    throw new Error('useFines must be used within a FinesProvider');
  }
  return context;
};
